import React, { useState } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import useAuth from '../hooks/useAuth';
import paymentService from '../services/paymentService';
import { Check, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import useExitIntent from '../hooks/useExitIntent';
import ExitIntentProModal from '../components/ui/ExitIntentProModal';

const PricingPage = () => {
  const { plans, loading, getCurrentPlan } = useSubscription();
  const { userProfile, user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const currentPlan = getCurrentPlan();

  // Exit-intent: only for users with no paid subscription (free tier or unauthenticated)
  const isUnsubscribed = !currentPlan || currentPlan.tier === 'free';
  const { showModal: showExitModal, dismiss: dismissExitModal } = useExitIntent({
    enabled: isUnsubscribed && !loading,
  });
  const proPlan = plans.find((p) => p.tier === 'pro') || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No pricing plans available at the moment.</p>
          <p className="text-sm text-gray-500">Please refresh the page or try again later.</p>
        </div>
      </div>
    );
  }

  const getFeatureList = (features) => {
    const featureDescriptions = {
      hires_per_month: (val) => val === -1 ? 'Unlimited hires & communication per month' : `${val} hires & communications per month`,
      active_job_posts: (val) => `Up to ${val} active job posts`,
      max_applications_per_job: (val) => `Max ${val} applications per job`,
      job_approval_time: (val) => val === 'instant' ? 'Instant job post approval' : '2-3 days job post approval',
      view_applications: () => 'View job applications',
      matchmaking_access: () => 'Access to VA matchmaking',
      interview_scheduling: () => 'Manage interviews & scheduling',
      max_hires: (val) => val === -1 ? 'Unlimited candidate hires' : `Hire up to ${val} candidate VAs`,
      ai_recommendations: (val) => val ? 'AI recommendations' : null,
      ai_job_posts_generation_credits: (val) => val === -1 ? 'Unlimited AI job post generation' : `${val} AI job post generations per month`
    };

    return Object.entries(features)
      .map(([key, value]) => {
        const description = featureDescriptions[key]?.(value);
        return description ? { key, description, included: true } : null;
      })
      .filter(Boolean);
  };

  const tierOrder = { free: 0, pro: 1, premium: 2, agency: 3 };

  const calculateSavings = (plan) => {
    const monthlyCost = plan.monthly_price * 12;
    const annualCost = plan.annual_price * 12;
    return monthlyCost - annualCost;
  };

  const handleSubscribe = async (tier) => {
    if (!user) {
      // Redirect to login if not authenticated
      window.location.href = '/login?redirect=/pricing';
      return;
    }

    if (userProfile?.role !== 'employer') {
      setCheckoutError('Only employers can subscribe to paid plans');
      return;
    }

    if (tier === 'free') {
      // Free tier doesn't require payment
      return;
    }

    setCheckoutLoading(tier);
    setCheckoutError(null);

    try {
      await paymentService.subscribeToplan({
        tier,
        billingCycle,
        userId: user.id,
      });
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutError(error.message || 'Failed to start checkout process');
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Choose Your Plan
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            Scale your virtual assistant hiring with the right plan for your needs
          </p>
        </div>

        {/* Error Display */}
        {checkoutError && (
          <div className="mt-8 max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{checkoutError}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setCheckoutError(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="mt-12 flex justify-center">
          <div className="relative bg-gray-200 p-1 rounded-lg flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm animate-pulse-glow'
                  : 'text-gray-500'
              }`}
            >
              Annual
              <span className="ml-1 text-green-600 text-sm font-bold">Save 42%</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-4">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan?.tier === plan.tier;
            const isPopular = plan.tier === 'pro';
            const price = billingCycle === 'annual' ? plan.annual_price : plan.monthly_price;
            const features = getFeatureList(plan.features);
            const savings = calculateSavings(plan);

            return (
              <div
                key={plan.tier}
                className={`relative rounded-2xl p-8 shadow-lg ${
                  isPopular && isUnsubscribed
                    ? 'border-2 border-blue-600 bg-white'
                    : 'border border-gray-200 bg-white'
                } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
              >
                {isPopular && isUnsubscribed && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="text-white px-4 py-1 rounded-full text-sm font-medium" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}>
                      Most Popular
                    </span>
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-4 right-4">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">${price}</span>
                    <span className="text-gray-500">/{billingCycle === 'annual' ? 'month, billed annually' : 'month'}</span>
                  </div>
                  
                  {billingCycle === 'annual' && plan.tier !== 'free' && (
                    <div className="mt-2">
                      <span className="text-sm text-green-600 font-medium">
                        Save ${savings.toFixed(2)} annually
                      </span>
                    </div>
                  )}
                </div>

                <ul className="mt-8 space-y-3">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="ml-3 text-gray-700">{feature.description}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  {userProfile?.role === 'employer' ? (
                    isCurrentPlan ? (
                      <Link
                        to="/employer-dashboard"
                        className="w-full block text-center px-6 py-3 rounded-lg font-medium bg-gray-100 text-gray-500 cursor-default transition-colors"
                      >
                        Current Plan
                      </Link>
                    ) : plan.tier === 'free' && !isUnsubscribed ? null : (
                      <button
                        onClick={() => handleSubscribe(plan.tier)}
                        disabled={checkoutLoading === plan.tier}
                        className={`w-full px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          isPopular
                            ? 'bg-blue-600 text-white hover:opacity-90 transition-opacity disabled:opacity-50'
                            : 'bg-gray-900 text-white hover:bg-gray-800 disabled:hover:bg-gray-900'
                        }`}
                      >
                        {checkoutLoading === plan.tier ? (
                          <div className="flex items-center justify-center">
                            <Loader2 className="animate-spin h-4 w-4 mr-2" />
                            Processing...
                          </div>
                        ) : (
                          plan.tier === 'free'
                            ? 'Get Started Free'
                            : !isUnsubscribed && tierOrder[plan.tier] > tierOrder[currentPlan?.tier]
                            ? 'Upgrade Plan'
                            : 'Subscribe Now'
                        )}
                      </button>
                    )
                  ) : (
                    <Link
                      to="/login-register?tab=register"
                      className="w-full block text-center px-6 py-3 rounded-lg font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                    >
                      Sign Up as Employer
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Can I change my plan anytime?</h3>
              <p className="text-gray-600">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect at your next billing cycle.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">What happens if I exceed my limits?</h3>
              <p className="text-gray-600">
                You'll receive notifications when approaching limits. Additional usage beyond your plan requires an upgrade.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Is there a free trial?</h3>
              <p className="text-gray-600">
                All new employers start with our Free Tier to test the platform before upgrading to a paid plan.
              </p>
            </div>
          </div>
        </div>

        {/* 30 Day Money Back Guarantee */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-12 shadow-sm border border-blue-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              30 Day Money Back Guarantee
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              We're confident you'll love our platform. If you're not completely satisfied within the first 30 days of your paid subscription, we'll refund your money—no questions asked.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-blue-600 mb-2">✓</div>
                <h3 className="font-semibold text-gray-900 mb-2">Full Refund</h3>
                <p className="text-sm text-gray-600">100% of your subscription cost refunded if you cancel within 30 days</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-blue-600 mb-2">✓</div>
                <h3 className="font-semibold text-gray-900 mb-2">No Hassle</h3>
                <p className="text-sm text-gray-600">Cancel anytime without providing a reason or dealing with support</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-blue-600 mb-2">✓</div>
                <h3 className="font-semibold text-gray-900 mb-2">Risk-Free Trial</h3>
                <p className="text-sm text-gray-600">Try all premium features risk-free to see if we're the right fit</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-8">
              Questions? <a href="mailto:support@vasprouts.com" className="text-blue-600 hover:text-blue-700 font-medium">Contact our support team</a> for assistance.
            </p>
          </div>
        </div>
      </div>

      {/* Exit-intent modal — Pro 1-month offer for unsubscribed users */}
      <ExitIntentProModal
        isOpen={showExitModal}
        onClose={dismissExitModal}
        proPlan={proPlan}
        user={user}
      />
    </div>
  );
};

export default PricingPage;